import React, { useEffect, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getCategoryTabId, getTabs } from '@client/redux/modules/mylounge';
import { findActiveTab } from '@client/components/mylounge/banner/helpers';
import {
    CATEGORY_TAB_ID_TRACK_MAPPING,
    TAB_CLICK_TRACK_EVENT_NAME,
} from '@client/components/mylounge/banner/constants';
import { track } from '@client/helpers/tracking';
import { scrollTo } from '@client/helpers';
import {
    CAMPAIGN_WRAPPER,
    M1_REBRANDING,
    THB_OUTER_WRAPPER,
} from '@client/components/mylounge/constants';
import { getElementRealHeight } from '@client/helpers/dom';
import { useConfig } from '@browsing/config';
import { tracker } from '@browsing/tracking';
import { SelectContentSchema } from '@pegasus/tracker';
import Tabs from './Tabs';

interface TabsProps {
    floating?: boolean;
    bannerTabsRef?: any;
}

export const TabsWrapper = ({ bannerTabsRef, floating }: TabsProps) => {
    const history = useHistory();
    const tabs = useSelector(getTabs);
    const config = useConfig();
    const categoryTabId = useSelector(getCategoryTabId);
    const [activeIndex, setActiveIndex] = useState(findActiveTab(tabs, categoryTabId));
    const [lastIndex, setLastIndex] = useState(null);
    const isM1Rebranding = config.isEnabled(M1_REBRANDING);

    const changeActiveTab = useCallback(
        (newActiveIndex: number) =>
            history.push(`/event/${tabs?.[newActiveIndex]?.id}${window.location.search}`),
        [history, tabs],
    );

    const isSameTab = useCallback(
        (newActiveIndex: number) => newActiveIndex === activeIndex,
        [activeIndex],
    );

    const trackTabEvent = useCallback(
        (eventName: string, index: number) => {
            const { id } = tabs[index];

            track(eventName, {
                campaign_level_2: (CATEGORY_TAB_ID_TRACK_MAPPING as any)[id] || id,
            });

            tracker.track('GA4-EEC-select_content', {
                event_name: 'select_content',
                select_content: {
                    element: 'category_tab',
                    content_type: 'click',
                    content_id: id,
                },
            });
        },
        [tabs],
    );

    const handleTabChange = useCallback(
        (newActiveIndex: number, trackEventName: string) => {
            if (isSameTab(newActiveIndex)) {
                return;
            }

            trackTabEvent(trackEventName, newActiveIndex);
            changeActiveTab(newActiveIndex);

            const wrapper = document.getElementById(CAMPAIGN_WRAPPER);
            const thbWrapper = document.getElementById(THB_OUTER_WRAPPER);

            if (wrapper) {
                if (isM1Rebranding) {
                    const height = getElementRealHeight(thbWrapper);
                    const { offsetTop } = wrapper;
                    const top = offsetTop - height;

                    scrollTo({ top });
                } else if (floating && bannerTabsRef && bannerTabsRef.current) {
                    const height = getElementRealHeight(bannerTabsRef.current);
                    const { offsetTop } = wrapper;

                    const top = offsetTop - height;

                    scrollTo({ top });
                }
            }
        },
        [isSameTab, trackTabEvent, changeActiveTab],
    );

    const handleTabClick = useCallback(
        (newActiveIndex: number) => handleTabChange(newActiveIndex, TAB_CLICK_TRACK_EVENT_NAME),
        [handleTabChange],
    );

    useEffect(() => {
        const newActiveIndex = findActiveTab(tabs, categoryTabId);
        if (newActiveIndex !== activeIndex) {
            setActiveIndex(newActiveIndex);
            setLastIndex(activeIndex);
        }
    }, [activeIndex, categoryTabId, tabs]);

    return (
        <Tabs
            tabs={tabs}
            onTabClick={handleTabClick}
            activeIndex={activeIndex}
            lastIndex={lastIndex}
        />
    );
};
